import { createClient, type SupabaseClient } from '@supabase/supabase-js';


// ── RATE LIMITING ──────────────────────────────────────────────────────────────
const LS_LOCKOUT_KEY = 'soleii_wl_lockout_until';
const COOLDOWN_SHORT_S = 30;       // 2nd attempt: 30 second cooldown
const COOLDOWN_LONG_S = 5 * 60;  // 3rd+ attempt: 5 minute cooldown (persisted)

let submitAttempts = 0;
let countdownInterval: ReturnType<typeof setInterval> | null = null;

function getPersistedLockoutRemaining(): number {
  const raw = localStorage.getItem(LS_LOCKOUT_KEY);
  if (!raw) return 0;
  const remaining = Math.ceil((Number(raw) - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
}

function startCooldown(seconds: number, persist: boolean): void {
  const btn = document.getElementById('submit-btn') as HTMLButtonElement | null;
  if (!btn) return;

  if (persist) {
    localStorage.setItem(LS_LOCKOUT_KEY, String(Date.now() + seconds * 1000));
  }

  if (countdownInterval !== null) clearInterval(countdownInterval);

  let remaining = seconds;

  const tick = (): void => {
    if (!btn) return;
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    const label = m > 0 ? `${m}m ${s}s` : `${s}s`;
    btn.textContent = `Try again in ${label} ⏳`;
    btn.disabled = true;
    btn.style.opacity = '0.6';

    if (remaining <= 0) {
      clearInterval(countdownInterval!);
      countdownInterval = null;
      btn.textContent = 'Join waitlist ✨';
      btn.disabled = false;
      btn.style.opacity = '1';
      setFormError(null);
    }

    remaining--;
  };

  tick();
  countdownInterval = setInterval(tick, 1000);
}

function checkRateLimit(): boolean {
  // Check persisted long lockout first
  const persisted = getPersistedLockoutRemaining();
  if (persisted > 0) {
    startCooldown(persisted, false);
    setFormError('Too many attempts. Please wait before trying again.');
    return false;
  }
  return true;
}

function applyPostSubmitCooldown(): void {
  submitAttempts++;
  if (submitAttempts >= 3) {
    startCooldown(COOLDOWN_LONG_S, true);
    setFormError('Too many attempts. Please wait 5 minutes before trying again.');
  } else if (submitAttempts >= 2) {
    startCooldown(COOLDOWN_SHORT_S, false);
  }
}
// ──────────────────────────────────────────────────────────────────────────────

function getClient(): SupabaseClient | null {
  const url = import.meta.env.VITE_SUPABASE_URL?.trim();
  const key =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() ||
    import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
  if (!url || !key) return null;
  return createClient(url, key);
}

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function generateStars(): void {
  const container = document.getElementById('bg-stars');
  if (!container) return;
  for (let i = 0; i < 80; i++) {
    const star = document.createElement('div');
    star.className = 'star';
    const size = Math.random() * 3 + 1;
    star.style.cssText = `
      width:${size}px;height:${size}px;
      top:${Math.random() * 100}%;
      left:${Math.random() * 100}%;
      --d:${2 + Math.random() * 4}s;
      --dl:${Math.random() * 4}s;
    `;
    container.appendChild(star);
  }
}

function buildSunRays(): void {
  const deco = document.getElementById('sun-deco');
  if (!deco) return;
  const count = 12;
  for (let i = 0; i < count; i++) {
    const ray = document.createElement('div');
    ray.className = 'sun-ray';
    const angle = (i / count) * 360;
    const radius = 38;
    const rad = ((angle - 90) * Math.PI) / 180;
    const x = 60 + Math.cos(rad) * radius;
    const y = 60 + Math.sin(rad) * radius;
    ray.style.cssText = `
      left:${x}px; top:${y}px;
      transform: rotate(${angle}deg) translateY(-100%);
      opacity:${0.3 + (i % 3) * 0.2};
    `;
    deco.appendChild(ray);
  }
  deco.style.animation = 'breathe 8s ease-in-out infinite';
}

// ── SCROLL REVEAL ─────────────────────────────────────────────────────────────
function initReveals(): void {
  const targets = document.querySelectorAll<HTMLElement>('[data-reveal]');
  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('in');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
  );
  targets.forEach((el) => io.observe(el));
}

// ── NAV: frost the bar once the hero starts scrolling away ───────────────────
function initNav(): void {
  const nav = document.getElementById('nav');
  if (!nav) return;
  const sync = (): void => {
    nav.classList.toggle('stuck', window.scrollY > 24);
  };
  sync();
  window.addEventListener('scroll', sync, { passive: true });
}

// ── AMBIENT AURA follows the pointer ─────────────────────────────────────────
function initAura(): void {
  if (prefersReducedMotion || !window.matchMedia('(pointer: fine)').matches) return;
  const aura = document.getElementById('bg-aura');
  if (!aura) return;
  document.body.classList.add('has-pointer');
  window.addEventListener(
    'pointermove',
    (e) => {
      aura.style.setProperty('--ax', `${e.clientX}px`);
      aura.style.setProperty('--ay', `${e.clientY}px`);
    },
    { passive: true },
  );
}

// ── MARQUEE: duplicate the track so translateX(-50%) loops seamlessly ────────
function initMarquee(): void {
  const track = document.getElementById('marquee-track');
  if (!track) return;
  track.innerHTML += track.innerHTML;
}

// ── HERO PHONES: gentle parallax drift toward the cursor ─────────────────────
// Translation only. A rotate here would skew the screenshots and read as if the
// app screens were cropped.
function initStageTilt(): void {
  if (prefersReducedMotion || !window.matchMedia('(pointer: fine)').matches) return;
  const stage = document.getElementById('hero-stage');
  if (!stage) return;

  let raf = 0;
  const onMove = (e: PointerEvent): void => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const r = stage.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      stage.style.transform = `translate3d(${(dx * 14).toFixed(1)}px, ${(dy * 8).toFixed(1)}px, 0)`;
    });
  };

  stage.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerleave', () => {
    stage.style.transform = '';
  });
}

// ── FEATURE SCROLLER: sticky phone swaps screenshot per active step ──────────
function initStepScroller(): void {
  const steps = Array.from(document.querySelectorAll<HTMLElement>('#steps .step'));
  const shots = Array.from(document.querySelectorAll<HTMLImageElement>('#swap img'));
  if (steps.length === 0 || shots.length === 0) return;

  const activate = (index: number): void => {
    steps.forEach((s, i) => s.classList.toggle('active', i === index));
    shots.forEach((img, i) => img.classList.toggle('on', i === index));
  };

  if (!('IntersectionObserver' in window)) {
    steps.forEach((s) => s.classList.add('active'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const shot = Number((entry.target as HTMLElement).dataset.shot);
        if (Number.isFinite(shot)) activate(shot);
      }
    },
    // narrow band across the middle of the viewport = one active step at a time
    { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
  );
  steps.forEach((s) => io.observe(s));
}

// ── "Join the waitlist" links drop the caret straight into the field ─────────
function initWaitlistLinks(): void {
  const input = document.getElementById('email-input') as HTMLInputElement | null;
  if (!input) return;
  document.querySelectorAll<HTMLAnchorElement>('a[href="#top"]').forEach((link) => {
    if (link.classList.contains('nav-logo')) return;
    link.addEventListener('click', () => {
      // wait for the smooth scroll to settle before stealing focus
      setTimeout(() => input.focus({ preventScroll: true }), prefersReducedMotion ? 0 : 650);
    });
  });
}

function setFormError(message: string | null): void {
  const el = document.getElementById('form-error');
  if (!el) return;
  if (!message) {
    el.hidden = true;
    el.textContent = '';
    return;
  }
  el.hidden = false;
  el.textContent = message;
}

function parseCountPayload(data: unknown): number | null {
  if (typeof data === 'number' && Number.isFinite(data)) return data;
  if (typeof data === 'string' && data.trim() !== '') {
    const n = Number(data);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

async function refreshPublicCount(sb: SupabaseClient): Promise<number | null> {
  const countEl = document.getElementById('waitlist-count');
  if (!countEl) return null;
  const { data, error } = await sb.rpc('waitlist_public_count');
  if (error) {
    countEl.textContent = '—';
    return null;
  }
  const n = parseCountPayload(data);
  if (n === null) {
    countEl.textContent = '—';
    return null;
  }
  countEl.textContent = String(n);
  return n;
}

async function handleSubmit(
  sb: SupabaseClient | null,
  e: SubmitEvent,
): Promise<void> {
  e.preventDefault();
  setFormError(null);

  if (!checkRateLimit()) return;

  const input = document.getElementById('email-input') as HTMLInputElement | null;
  const btn = document.getElementById('submit-btn') as HTMLButtonElement | null;
  const formBox = document.getElementById('form-container');
  const success = document.getElementById('success-state');
  const spotEl = document.getElementById('spot-num');

  if (!input || !btn || !formBox || !success || !spotEl) return;

  const raw = input.value.trim();
  if (!raw) return;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(raw)) {
    setFormError('Please enter a valid email address.');
    return;
  }

  if (!sb) {
    setFormError(
      'Waitlist is not configured yet (set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY).',
    );
    return;
  }

  const email = raw.toLowerCase();
  btn.textContent = 'Adding you...';
  btn.disabled = true;
  btn.style.opacity = '0.7';

  const { error } = await sb.from('waitlist_emails').insert({ email });

  if (error) {
    btn.textContent = 'Join waitlist ✨';
    btn.disabled = false;
    btn.style.opacity = '1';
    applyPostSubmitCooldown();
    if (error.code === '23505') {
      setFormError('That email is already on the list.');
      return;
    }
    setFormError(error.message || 'Something went wrong. Try again in a moment.');
    return;
  }

  const total = await refreshPublicCount(sb);
  if (total !== null) spotEl.textContent = String(total);

  formBox.style.display = 'none';
  success.classList.add('show');
  setTimeout(() => {
    success.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 100);
}

const sb = getClient();

generateStars();
buildSunRays();
initReveals();
initNav();
initAura();
initMarquee();
initStageTilt();
initStepScroller();
initWaitlistLinks();

// Re-apply lockout countdown on page load if still active
const initialLockout = getPersistedLockoutRemaining();
if (initialLockout > 0) {
  submitAttempts = 3;
  startCooldown(initialLockout, false);
}

if (sb) {
  void refreshPublicCount(sb);
} else {
  const countEl = document.getElementById('waitlist-count');
  if (countEl) countEl.textContent = '—';
}

document.getElementById('waitlist-form')?.addEventListener('submit', (e) => {
  void handleSubmit(sb, e);
});

// ── TICKET RENDERER ──────────────────────────────────────────────────────────
const logoReady: Promise<HTMLImageElement> = new Promise((resolve) => {
  const img = new Image();
  img.src = '/logo.png';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(img);
});

async function renderTicket(spot: string): Promise<void> {
  const canvas = document.getElementById('ticket-canvas') as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = 540, H = 320;
  canvas.width = W * 2;
  canvas.height = H * 2;
  ctx.scale(2, 2);

  // Background
  const bgGrad = ctx.createLinearGradient(0, 0, W, H);
  bgGrad.addColorStop(0, '#140A1E');
  bgGrad.addColorStop(0.5, '#241338');
  bgGrad.addColorStop(1, '#140A1E');
  ctx.fillStyle = bgGrad;
  ctx.beginPath();
  ctx.roundRect(0, 0, W, H, 16);
  ctx.fill();

  // Diagonal lines (subtle)
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.strokeStyle = '#F0709A';
  ctx.lineWidth = 0.5;
  for (let i = -H; i < W + H; i += 24) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + H, H);
    ctx.stroke();
  }
  ctx.restore();

  // Radial glow top-left
  const glow1 = ctx.createRadialGradient(80, 60, 0, 80, 60, 200);
  glow1.addColorStop(0, 'rgba(240, 112, 154, 0.20)');
  glow1.addColorStop(1, 'transparent');
  ctx.fillStyle = glow1;
  ctx.fillRect(0, 0, W, H);

  // Radial glow bottom-right
  const glow2 = ctx.createRadialGradient(W - 60, H - 40, 0, W - 60, H - 40, 160);
  glow2.addColorStop(0, 'rgba(245, 184, 65, 0.12)');
  glow2.addColorStop(1, 'transparent');
  ctx.fillStyle = glow2;
  ctx.fillRect(0, 0, W, H);

  // Stars
  ctx.fillStyle = '#FFD37A';
  for (let i = 0; i < 30; i++) {
    const sx = Math.random() * W;
    const sy = Math.random() * H;
    const sr = Math.random() * 1.2 + 0.3;
    ctx.globalAlpha = Math.random() * 0.4 + 0.1;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Border
  ctx.strokeStyle = 'rgba(253, 246, 236, 0.20)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(1, 1, W - 2, H - 2, 16);
  ctx.stroke();

  // Inner border (gold foil edge)
  ctx.strokeStyle = 'rgba(255, 211, 122, 0.10)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.roundRect(8, 8, W - 16, H - 16, 12);
  ctx.stroke();

  // Tear line (perforated left edge)
  ctx.setLineDash([4, 6]);
  ctx.strokeStyle = 'rgba(253, 246, 236, 0.18)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(100, 20);
  ctx.lineTo(100, H - 20);
  ctx.stroke();
  ctx.setLineDash([]);

  // Left section — number
  ctx.save();
  ctx.translate(50, H / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.font = '700 10px Nunito, sans-serif';
  ctx.fillStyle = 'rgba(253, 246, 236, 0.42)';
  ctx.letterSpacing = '2px';
  ctx.textAlign = 'center';
  ctx.fillText('ADMIT ONE', 0, 0);
  ctx.restore();

  // Logo
  ctx.font = '900 24px "Fredoka One", cursive';
  const logoGrad = ctx.createLinearGradient(130, 40, 260, 40);
  logoGrad.addColorStop(0, '#F0709A');
  logoGrad.addColorStop(1, '#FFD37A');
  ctx.fillStyle = logoGrad;
  ctx.textAlign = 'left';
  ctx.fillText('Soleii', 130, 52);

  // Badge
  ctx.font = '800 9px Nunito, sans-serif';
  ctx.fillStyle = 'rgba(251, 197, 216, 0.7)';
  ctx.fillText('FOUNDING MEMBER', 130, 72);

  // Spot number (big)
  ctx.font = '900 72px "Fredoka One", cursive';
  const numGrad = ctx.createLinearGradient(130, 110, 130, 200);
  numGrad.addColorStop(0, '#FFD37A');
  numGrad.addColorStop(1, '#F0709A');
  ctx.fillStyle = numGrad;
  ctx.fillText(`#${spot}`, 130, 185);

  // Subtitle below number
  ctx.font = '700 14px Nunito, sans-serif';
  ctx.fillStyle = 'rgba(253, 246, 236, 0.72)';
  ctx.fillText('on the waitlist', 134, 210);

  // Tagline
  ctx.font = '600 12px Nunito, sans-serif';
  ctx.fillStyle = 'rgba(253, 246, 236, 0.45)';
  ctx.fillText('Cycle tracking app for couples', 130, 270);

  // URL
  ctx.font = '800 11px Nunito, sans-serif';
  ctx.fillStyle = 'rgba(255, 211, 122, 0.8)';
  ctx.fillText('soleii.pro', 130, 292);

  // Right side — logo
  const logoImg = await logoReady;
  const logoSize = 90;
  const logoX = W - 70 - logoSize / 2;
  const logoY = H / 2 - logoSize / 2;

  // Glow behind logo
  ctx.save();
  const logoGlow = ctx.createRadialGradient(logoX + logoSize / 2, logoY + logoSize / 2, 0, logoX + logoSize / 2, logoY + logoSize / 2, logoSize * 0.8);
  logoGlow.addColorStop(0, 'rgba(240, 112, 154, 0.20)');
  logoGlow.addColorStop(1, 'transparent');
  ctx.fillStyle = logoGlow;
  ctx.beginPath();
  ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize * 0.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
}

function getTicketBlob(): Promise<Blob | null> {
  const canvas = document.getElementById('ticket-canvas') as HTMLCanvasElement | null;
  if (!canvas) return Promise.resolve(null);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}

// Render ticket when success state shows
const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    if (m.type === 'attributes' && m.attributeName === 'class') {
      const el = m.target as HTMLElement;
      if (el.classList.contains('show')) {
        const spot = document.getElementById('spot-num')?.textContent || '1';
        void renderTicket(spot);
      }
    }
  }
});
const successEl = document.getElementById('success-state');
if (successEl) observer.observe(successEl, { attributes: true });

// Share button
document.getElementById('share-btn')?.addEventListener('click', async () => {
  const spot = document.getElementById('spot-num')?.textContent || '';
  const blob = await getTicketBlob();

  if (blob && navigator.share && navigator.canShare?.({ files: [new File([blob], 'soleii-ticket.png', { type: 'image/png' })] })) {
    const file = new File([blob], 'soleii-ticket.png', { type: 'image/png' });
    try {
      await navigator.share({
        title: 'Soleii Waitlist',
        text: `I'm #${spot} on the Soleii waitlist — the cycle tracking app for couples 🌙\nJoin here: https://soleii.pro`,
        files: [file],
      });
    } catch (_) { /* cancelled */ }
  } else if (blob) {
    await navigator.clipboard.writeText(`I'm #${spot} on the Soleii waitlist — the cycle tracking app for couples 🌙\nJoin: https://soleii.pro`);
    const btn = document.getElementById('share-btn');
    if (btn) {
      const orig = btn.innerHTML;
      btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg> Link copied!';
      setTimeout(() => { btn.innerHTML = orig; }, 2500);
    }
  }
});

// Download button
document.getElementById('download-btn')?.addEventListener('click', async () => {
  const blob = await getTicketBlob();
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'soleii-waitlist-ticket.png';
  a.click();
  URL.revokeObjectURL(url);
});
